/**
 * SettingsPage — app preferences.
 * Sections: Account, Languages, Daily goal, Notifications, Connected accounts, Dev tools (DEV only)
 */
import { Fragment, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { BellOff, Check, Globe } from 'lucide-react'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { useAuthStore, selectUser, selectSetUser } from '../stores/authStore'
import apiClient, { changePassword, extractErrorMessage } from '../api/client'
import { getLangMeta } from '../utils/languages'
import { isLanguageNotReleased } from '../utils/languageWriteError'
import { useLocale } from '../i18n/context'
import { APP_TRANSLATIONS, resolveLocale, type AppTranslations } from '../i18n/app'
import { selectableInterfaceLanguages, useLanguageRegistry } from '../i18n/registry'
import type { User } from '../types'
import {
  listLinkedIdentities,
  startOAuth,
  unlinkProvider,
  type LinkedIdentity,
} from '../api/oauth'

export default function SettingsPage() {
  const user    = useAuthStore(selectUser)
  const setUser = useAuthStore(selectSetUser)
  const [devToggling, setDevToggling] = useState(false)
  const { tr } = useLocale()
  const s = tr.settings

  const handleDevTogglePro = async () => {
    setDevToggling(true)
    try {
      const { data } = await apiClient.post<User>('/api/v1/debug/toggle-pro/')
      setUser(data)
    } finally {
      setDevToggling(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
        {s.title}
      </h1>

      {/* Account info section */}
      {user && <AccountSection user={user} />}

      {/* Password section */}
      {user && <PasswordSection user={user} />}

      {/* Languages section */}
      {user && <LanguagesSection user={user} setUser={setUser} />}

      {/* Daily goal section */}
      {user && <DailyGoalSection user={user} setUser={setUser} />}

      {/* Connected accounts (OAuth) */}
      {user && <ConnectedAccountsSection user={user} />}

      {/* Notifications section */}
      <NotificationsSection />

      {/* Dev-only section */}
      {import.meta.env.DEV && (
        <section
          style={{
            backgroundColor: 'var(--bg-surface)',
            border: '1px dashed var(--border)',
            borderRadius: 16,
            padding: '20px 24px',
            maxWidth: 520,
            opacity: 0.85,
          }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16 }}>
            {s.devTools.title}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <p style={{ margin: 0, fontWeight: 500, fontSize: 15, color: 'var(--text-primary)', marginBottom: 2 }}>
                {s.devTools.togglePro}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                {s.devTools.currentPlan} <strong>{user?.plan ?? '—'}</strong>
              </p>
            </div>
            <button
              onClick={handleDevTogglePro}
              disabled={devToggling}
              style={{
                flexShrink: 0,
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-page)',
                color: 'var(--text-primary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: devToggling ? 'wait' : 'pointer',
                opacity: devToggling ? 0.6 : 1,
              }}
            >
              {devToggling ? s.devTools.switching : user?.plan === 'pro' ? s.devTools.switchToFree : s.devTools.switchToPro}
            </button>
          </div>
        </section>
      )}
    </div>
  )
}

// ── Notifications section ─────────────────────────────────────────────────────

/**
 * Daily study reminders.
 *
 * Every state this control can be in is distinct on screen and named: on, off,
 * blocked by the browser, unsupported here, still being worked out, and the
 * three ways the account and this device can disagree. A state that cannot be
 * acted on is never rendered as a plain greyed switch under an encouraging
 * hint — "blocked", "unsupported" and "checking" used to be indistinguishable
 * from a control that simply did nothing.
 *
 * The state accounts for what the account holds (`GET /push/status`), not only
 * what this browser holds. Reminders are sent on the strength of the account
 * flag, so a person on a new device is told that reminders are on and going
 * elsewhere rather than being shown a switch that reads "off".
 */
function NotificationsSection() {
  const { tr } = useLocale()
  const s = tr.settings.notifications
  const { state, issue, isLoading, enable, disable, refresh, serverStatus } =
    usePushNotifications({ withServerStatus: true })

  // Which direction is in flight, so the caption can say which.
  const [pending, setPending] = useState<'on' | 'off' | null>(null)

  // A switch is shown for every state it can actually move. `blocked` and
  // `unsupported` get a labelled status chip instead — the point is that they
  // do not look like a switch at all.
  const showsSwitch = state !== 'blocked' && state !== 'unsupported'
  const accountOn = state === 'accountOnDeviceOff' || state === 'accountOnNoDevice'
  const checked = state === 'on' || accountOn

  const STATUS: Record<typeof state, { word: string; color: string }> = {
    checking:           { word: s.statusChecking,    color: 'var(--text-secondary)' },
    unsupported:        { word: s.statusUnsupported, color: 'var(--text-secondary)' },
    blocked:            { word: s.statusBlocked,     color: 'var(--warning)' },
    on:                 { word: s.statusOn,          color: 'var(--success)' },
    off:                { word: s.statusOff,         color: 'var(--text-secondary)' },
    accountOnDeviceOff: { word: s.statusOn,          color: 'var(--warning)' },
    accountOnNoDevice:  { word: s.statusOn,          color: 'var(--warning)' },
    deviceOnAccountOff: { word: s.statusOff,         color: 'var(--warning)' },
  }

  const HINT: Record<typeof state, string> = {
    checking:           s.hintChecking,
    unsupported:        s.hintUnsupported,
    blocked:            s.hintBlocked,
    on:                 s.hintOn,
    off:                s.hintOff,
    accountOnDeviceOff: s.hintAccountOnDeviceOff,
    accountOnNoDevice:  s.hintAccountOnNoDevice,
    deviceOnAccountOff: s.hintDeviceOnAccountOff,
  }

  const ISSUE_TEXT: Record<NonNullable<typeof issue>, string> = {
    dismissed:         s.issueDismissed,
    refused:           s.issueRefused,
    blocked:           s.issueBlocked,
    workerUnavailable: s.issueWorker,
    keyUnavailable:    s.issueKey,
    subscribeFailed:   s.issueSubscribe,
    registerFailed:    s.issueRegister,
    disableFailed:     s.issueDisable,
    statusUnavailable: s.issueStatus,
  }

  // A retry is offered only where pressing again can plausibly do something
  // different. A refusal and a standing block cannot be retried from here —
  // they are fixed in the browser's own settings, and their copy says where.
  const RETRYABLE: Partial<Record<NonNullable<typeof issue>, 'enable' | 'disable' | 'refresh'>> = {
    workerUnavailable: 'enable',
    keyUnavailable:    'enable',
    subscribeFailed:   'enable',
    registerFailed:    'enable',
    disableFailed:     'disable',
    statusUnavailable: 'refresh',
  }

  const run = async (direction: 'on' | 'off') => {
    setPending(direction)
    try {
      if (direction === 'on') await enable()
      else await disable()
    } finally {
      setPending(null)
    }
  }

  const handleToggle = () => {
    if (isLoading) return
    void run(checked ? 'off' : 'on')
  }

  const showsResolveAction = accountOn || state === 'deviceOnAccountOff'
  const retryKind = issue ? RETRYABLE[issue] : undefined
  const handleRetry = () => {
    if (retryKind === 'refresh') { void refresh(); return }
    void run(retryKind === 'disable' ? 'off' : 'on')
  }

  // The caption is the busy line while a press is in flight, the issue while
  // one is outstanding, and otherwise the state's own explanation.
  const caption = isLoading
    ? (pending === 'off' ? s.busyOff : s.busyOn)
    : issue
      ? ISSUE_TEXT[issue]
      : HINT[state]
  const captionColor = isLoading
    ? 'var(--text-secondary)'
    : issue
      ? (issue === 'refused' || issue === 'blocked' || issue === 'dismissed' || issue === 'statusUnavailable'
          ? 'var(--warning)'
          : 'var(--danger)')
      : 'var(--text-secondary)'

  // Reminders can be on for the account while this browser is blocked. Saying
  // only "blocked" there would hide the fact that they are still arriving
  // elsewhere, and hide that the switch could still turn them off everywhere.
  const showsBlockedAccountNote = state === 'blocked' && serverStatus?.notifications_enabled === true

  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 24px',
        maxWidth: 520,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
        {s.title}
      </h2>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontWeight: 500, fontSize: 15, color: 'var(--text-primary)' }}>
              {s.dailyReminders}
            </p>
            {/* The state, in a word, for every state — not only the ones the
                switch position can express. */}
            <span
              data-testid="notif-status"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: STATUS[state].color,
              }}
            >
              {STATUS[state].word}
            </span>
          </div>

          <p
            data-testid="notif-caption"
            style={{ margin: 0, fontSize: 13, color: captionColor, lineHeight: 1.5 }}
          >
            {caption}
          </p>

          {showsBlockedAccountNote && (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {s.hintAccountOnDeviceOff}
            </p>
          )}

          {/* The action that resolves a disagreement between the account and
              this device, offered directly rather than left to be inferred
              from a switch whose position already reads as correct. */}
          {!isLoading && showsResolveAction && (
            <button
              onClick={() => void run('on')}
              style={secondaryActionStyle}
            >
              {state === 'deviceOnAccountOff' ? s.actionFinish : s.actionEnableHere}
            </button>
          )}

          {/* Never alongside the resolve action — after a failed attempt from
              one of the disagreement states, both would be the same press. */}
          {!isLoading && !showsResolveAction && retryKind && (
            <button onClick={handleRetry} style={secondaryActionStyle}>
              {s.actionRetry}
            </button>
          )}
        </div>

        {showsSwitch ? (
          <button
            onClick={handleToggle}
            disabled={isLoading || state === 'checking'}
            aria-checked={checked}
            aria-busy={isLoading}
            role="switch"
            aria-label={s.ariaToggle}
            style={{
              flexShrink: 0,
              width: 48,
              height: 28,
              borderRadius: 50,
              border: 'none',
              cursor: isLoading ? 'wait' : state === 'checking' ? 'progress' : 'pointer',
              backgroundColor: checked ? 'var(--accent)' : 'var(--border)',
              position: 'relative',
              transition: 'background-color 0.2s',
              padding: 0,
              // `checking` is neither on nor off, and is not drawn as either:
              // the knob rests mid-track so the control reads as undecided
              // rather than as a switch that is off and refusing to move.
              opacity: state === 'checking' ? 0.6 : 1,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 4,
                left: state === 'checking' ? 14 : checked ? 24 : 4,
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#fff',
                transition: 'left 0.2s',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}
            />
          </button>
        ) : (
          <span
            data-testid="notif-chip"
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 999,
              border: `1px solid ${state === 'blocked' ? 'var(--warning)' : 'var(--border)'}`,
              backgroundColor: state === 'blocked' ? 'var(--warning-soft)' : 'var(--bg-secondary)',
              color: state === 'blocked' ? 'var(--warning)' : 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <BellOff size={14} strokeWidth={2} />
            {STATUS[state].word}
          </span>
        )}
      </div>
    </section>
  )
}

// Shared look for the notifications section's inline actions (resolve a
// disagreement, retry a failed attempt) — a quiet button, not a second switch.
const secondaryActionStyle: CSSProperties = {
  marginTop: 10,
  padding: '8px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-page)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  minHeight: 40,
}

// ── Account section ───────────────────────────────────────────────────────────

function AccountSection({ user }: { user: User }) {
  const { tr } = useLocale()
  const s = tr.settings.account
  const now = new Date()

  // Compute plan label inline so it can use translated strings
  let planText = s.planFree
  let planColor = 'var(--text-secondary)'
  if (user.trial_ends_at) {
    const trialEnd = new Date(user.trial_ends_at)
    if (trialEnd > now) {
      const days = Math.ceil((trialEnd.getTime() - now.getTime()) / 86_400_000)
      planText = s.planTrial(days)
      planColor = 'var(--accent)'
    }
  }
  if (planText === s.planFree && user.plan === 'pro') {
    if (!user.plan_expires_at) {
      planText = s.planProLifetime
      planColor = 'var(--accent)'
    } else {
      const expires = new Date(user.plan_expires_at)
      if (expires > now) {
        planText = s.planProRenews(expires.toISOString().split('T')[0])
        planColor = 'var(--accent)'
      }
    }
  }

  // Active trial: positive days remaining, not yet converted to paid Pro
  const trialDaysLeft = (() => {
    if (!user.trial_ends_at || user.plan === 'pro') return null
    const ms = new Date(user.trial_ends_at).getTime() - Date.now()
    if (ms <= 0) return null
    return Math.ceil(ms / 86_400_000)
  })()

  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 24px',
        maxWidth: 520,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
        {s.title}
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Email */}
        <div>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.email}
          </p>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', wordBreak: 'break-all' }}>
            {user.email}
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{s.readOnly}</span>
          </p>
        </div>

        {/* Display name */}
        {user.display_name && (
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {s.displayName}
            </p>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
              {user.display_name}
            </p>
          </div>
        )}

        {/* Plan */}
        <div>
          <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.plan}
          </p>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: planColor }}>
            {planText}
          </p>
        </div>

        {/* Neutral trial status line — only shown while an active trial exists */}
        {trialDaysLeft !== null && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, padding: '10px 14px',
            borderRadius: 10, border: '1px solid var(--border)',
            backgroundColor: 'var(--bg-page)',
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
              {s.trialDaysLeft(trialDaysLeft)}
            </span>
            <Link
              to="/pro"
              style={{
                fontSize: 12, fontWeight: 600,
                color: 'var(--accent)', textDecoration: 'none',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {s.upgrade}
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

// ── Password section ──────────────────────────────────────────────────────────

// Shared input styling — matches DESIGN_SYSTEM.md "Input Fields" spec
// (--bg-secondary bg, 1px --border, --radius-sm, focus → --accent).
const passwordInputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 16px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  backgroundColor: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: 15,
  fontFamily: 'var(--font-ui)',
  outline: 'none',
}

function PasswordSection({ user }: { user: User }) {
  const { tr } = useLocale()
  const s = tr.settings.password

  // Older persisted User objects predate the flag — default to "has a password".
  const hasPassword = user.has_password ?? true

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sectionStyle: CSSProperties = {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '20px 24px',
    maxWidth: 520,
  }

  // OAuth-only account (ADR-026): no password to change. Reset flow that would
  // add one is not built yet — show an honest caption, no form.
  if (!hasPassword) {
    return (
      <section style={sectionStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
          {s.title}
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {s.oauthOnly}
        </p>
      </section>
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (saving) return
    setError(null)
    setSaved(false)

    // Client-side guards mirror the backend: length-only (ADR-060 S-5) + match.
    if (next.length < 8) {
      setError(s.errTooShort)
      return
    }
    if (next !== confirm) {
      setError(s.errMismatch)
      return
    }

    setSaving(true)
    try {
      await changePassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      // 403 INVALID_CREDENTIALS is the wrong-current-password refusal — a domain
      // rejection, deliberately NOT a 401. The interceptor signs the user out on
      // a 401 from an auth route (session expiry), so the backend never uses 401
      // here; this inline error leaves the session untouched.
      setError(status === 403 ? s.errWrongCurrent : extractErrorMessage(err, s.errGeneric))
    } finally {
      setSaving(false)
    }
  }

  const canSubmit = !saving && current.length > 0 && next.length > 0 && confirm.length > 0

  return (
    <section style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          {s.title}
        </h2>
        {saved && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--success)', fontWeight: 500 }}>
            <Check size={14} strokeWidth={2.5} />
            {s.changed}
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {s.desc}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Hidden username field helps password managers associate the change. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={user.email}
          readOnly
          hidden
          aria-hidden
        />
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.current}
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            style={passwordInputStyle}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
            onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.newPassword}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            style={passwordInputStyle}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
            onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {s.confirm}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={passwordInputStyle}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
            onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
          />
        </label>

        {error && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--accent-soft)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 12px',
              color: 'var(--danger)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '0 24px',
              height: 44,
              borderRadius: 'var(--radius-pill)',
              border: 'none',
              backgroundColor: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.6,
              transition: 'background-color 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.backgroundColor = 'var(--accent-hover)' }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-fill)' }}
          >
            {saving ? s.saving : s.submit}
          </button>
        </div>
      </form>
    </section>
  )
}

// ── Daily goal section ────────────────────────────────────────────────────────

interface DailyGoalSectionProps {
  user: User
  setUser: (u: User) => void
}

function DailyGoalSection({ user, setUser }: DailyGoalSectionProps) {
  const { tr } = useLocale()
  const s = tr.settings.dailyGoal
  const currentGoal = user.daily_card_goal ?? 40
  const [selected, setSelected] = useState(currentGoal)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const queryClient = useQueryClient()

  const SESSION_OPTIONS: { value: number; label: string }[] = [
    { value: 40,  label: s.option1 },
    { value: 80,  label: s.option2 },
    { value: 120, label: s.option3 },
  ]

  const handleSelect = async (val: number) => {
    if (val === selected || saving) return
    setSelected(val)
    setSaving(true)
    setSaved(false)
    try {
      const { data } = await apiClient.patch<User>('/api/v1/auth/me', { daily_card_goal: val })
      setUser(data)
      // Home/Sidebar/Progress read the goal off ['progress-summary'], a separate
      // cache from the user object — without this the new goal only appears once
      // something else happens to refetch it (e.g. starting a session).
      void queryClient.invalidateQueries({ queryKey: ['progress-summary'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 24px',
        maxWidth: 520,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          {s.title}
        </h2>
        {saved && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--success)', fontWeight: 500 }}>
            <Check size={14} strokeWidth={2.5} />
            {s.saved}
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {s.desc}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {SESSION_OPTIONS.map(({ value, label }) => {
          const isActive = selected === value
          return (
            <button
              key={value}
              onClick={() => handleSelect(value)}
              disabled={saving}
              aria-pressed={isActive}
              style={{
                padding: '8px 20px',
                borderRadius: 50,
                border: `1.5px solid ${isActive ? 'var(--accent-fill)' : 'var(--border)'}`,
                backgroundColor: isActive ? 'var(--accent-fill)' : 'var(--bg-page)',
                color: isActive ? 'var(--text-on-accent)' : 'var(--text-primary)',
                fontSize: 14,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
                fontFamily: 'var(--font-ui)',
                opacity: saving && !isActive ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isActive && !saving) {
                  e.currentTarget.style.borderColor = 'var(--accent)'
                  e.currentTarget.style.backgroundColor = 'var(--accent-soft)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor = 'var(--border)'
                  e.currentTarget.style.backgroundColor = 'var(--bg-page)'
                }
              }}
            >
              {label}
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ── Show-translations row (used inside LanguagesSection) ──────────────────────

function ShowTranslationsRow({
  user,
  setUser,
  saving,
  setSaving,
}: {
  user: User
  setUser: (u: User) => void
  saving: boolean
  setSaving: (v: boolean) => void
}) {
  const { tr } = useLocale()
  const s = tr.settings.translations
  // Self-referential pair (e.g. en→en): no translations can exist for this pair.
  // ADR-050 Q1: disable-with-caption, never hidden.
  const noTranslationsForPair = user.source_language === user.target_language

  const currentValue = user.show_translations ?? true
  const isDisabled = noTranslationsForPair || saving

  const handleToggle = async () => {
    if (isDisabled) return
    setSaving(true)
    try {
      const { data } = await apiClient.patch<User>('/api/v1/auth/me', {
        show_translations: !currentValue,
      })
      setUser(data)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {s.sectionTitle}
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 500, fontSize: 15, color: isDisabled ? 'var(--text-secondary)' : 'var(--text-primary)', marginBottom: 4 }}>
            {s.showTitle}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {noTranslationsForPair ? s.notAvailable : s.showDesc}
          </p>
        </div>

        <button
          onClick={handleToggle}
          disabled={isDisabled}
          aria-checked={currentValue}
          role="switch"
          aria-label={s.ariaToggle}
          style={{
            flexShrink: 0,
            width: 48,
            height: 28,
            borderRadius: 50,
            border: 'none',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            backgroundColor: currentValue ? 'var(--accent)' : 'var(--border)',
            position: 'relative',
            transition: 'background-color 0.2s',
            padding: 0,
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 4,
              left: currentValue ? 24 : 4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}
          />
        </button>
      </div>
    </div>
  )
}

// ── Native language row (used inside LanguagesSection) ────────────────────────

interface SourceLanguageOption {
  code: string
  name: string
  native_name: string
}

function NativeLanguageRow({
  user,
  setUser,
  saving,
  setSaving,
}: {
  user: User
  setUser: (u: User) => void
  saving: boolean
  setSaving: (v: boolean) => void
}) {
  const { tr } = useLocale()
  const s = tr.settings.nativeLanguage
  const [options, setOptions] = useState<SourceLanguageOption[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Native name of the account's current value when it is not on the offered list.
  const [heldName, setHeldName] = useState<string | null>(null)

  // Selectable native languages depend on the active target_language — refetch
  // whenever it changes (e.g. user switches their active learning language).
  useEffect(() => {
    if (!user.target_language) {
      setOptions([])
      return
    }
    let cancelled = false
    apiClient
      .get<{ languages: SourceLanguageOption[] }>('/api/v1/languages/source-options/', {
        params: { target_language: user.target_language },
      })
      .then(({ data }) => { if (!cancelled) setOptions(data.languages) })
      .catch(() => { if (!cancelled) setOptions([]) })
    return () => { cancelled = true }
  }, [user.target_language])

  // An account can hold a language that is not (or no longer) on the offered list:
  // supported but unreleased, kept as it was (ADR-111 Amendment 1). Its value is
  // shown as it is, never as an empty select; the list itself offers released
  // languages only.
  const held = user.source_language
  const listed = (options ?? []).some((o) => o.code === held)
  useEffect(() => {
    if (!options || !held || listed) return
    let cancelled = false
    apiClient
      .get<{ languages: SourceLanguageOption[] }>('/api/v1/languages/')
      .then(({ data }) => {
        const hit = data.languages.find((l) => l.code === held)
        if (!cancelled && hit) setHeldName(hit.native_name)
      })
      .catch(() => { /* the English name below stands in */ })
    return () => { cancelled = true }
  }, [options, held, listed])

  const handleChange = async (code: string) => {
    if (!code || code === user.source_language || saving) return
    setSaving(true)
    setError(null)
    try {
      const { data } = await apiClient.patch<User>('/api/v1/auth/me', { source_language: code })
      setUser(data)
    } catch (err) {
      if (!isLanguageNotReleased(err)) throw err
      setError(tr.settings.languages.notReleased)
    } finally {
      setSaving(false)
    }
  }

  const currentValue = options && held ? held : ''
  const isDisabled = saving || !options || options.length === 0

  return (
    <div>
      <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {s.title}
      </p>
      <p style={{ margin: '8px 0 12px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {s.desc}
      </p>

      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <select
          value={currentValue}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isDisabled}
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            paddingLeft: 16,
            paddingRight: 32,
            paddingTop: 9,
            paddingBottom: 9,
            border: '1px solid var(--border)',
            borderRadius: 50,
            backgroundColor: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'var(--font-ui)',
            cursor: isDisabled ? 'wait' : 'pointer',
            outline: 'none',
            minWidth: 160,
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
          onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
        >
          {!currentValue && <option value="">{s.choose}</option>}
          {options && held && !listed && (
            <option value={held}>{heldName ?? getLangMeta(held).label}</option>
          )}
          {(options ?? []).map(({ code, native_name }) => (
            <option key={code} value={code}>{native_name}</option>
          ))}
        </select>
        <span style={{ position: 'absolute', right: 10, pointerEvents: 'none', color: 'var(--text-secondary)', fontSize: 12 }} aria-hidden>▾</span>
      </div>
      {error && (
        <p role="alert" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--danger)' }}>{error}</p>
      )}
    </div>
  )
}

// ── The interface-language keep/revert prompt (ADR-111 D7) ──────────────────────
// Reads "the new language · English", so a user who switched to a language they
// cannot read still finds the way back. Not routed through the active locale
// alone for that reason. When the new language is English, English once.
type LanguageStrings = AppTranslations['settings']['languages']

function revertPromptParts(newTable: AppTranslations, newCode: string, pick: (s: LanguageStrings) => string): string[] {
  const english = pick(APP_TRANSLATIONS.en.settings.languages)
  return newCode === 'en' ? [english] : [pick(newTable.settings.languages), english]
}

function revertPromptText(newTable: AppTranslations, newCode: string, pick: (s: LanguageStrings) => string): string {
  return revertPromptParts(newTable, newCode, pick).join(' · ')
}

const CONFIRM_SECONDS = 8

// ── Languages section ─────────────────────────────────────────────────────────

interface LanguagesSectionProps {
  user: User
  setUser: (u: User) => void
}

function LanguagesSection({ user, setUser }: LanguagesSectionProps) {
  const { setLocale, tr, locale } = useLocale()
  const s = tr.settings.languages
  const registry = useLanguageRegistry()
  // Offered: an interface table, and released (ADR-111 D5). An account holding
  // something else keeps it, shown as itself (ADR-111 Amendment 1, clause 2).
  const interfaceOptions = selectableInterfaceLanguages(registry)
  const heldInterface = user.interface_language
  const heldListed = interfaceOptions.some((o) => o.code === heldInterface)
  const nativeNameOf = (code: string) =>
    registry?.find((l) => l.code === code)?.nativeName ?? getLangMeta(code).label
  const [saving, setSaving] = useState(false)
  const [langError, setLangError] = useState<string | null>(null)

  // Tracks whether the banner actions should stack (≤389px). JS-driven so the
  // breakpoint is guaranteed to fire at the right viewport width (injected
  // <style> media queries are unreliable inside nested React trees).
  const [isNarrowBanner, setIsNarrowBanner] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 389
  )
  useEffect(() => {
    const check = () => setIsNarrowBanner(window.innerWidth <= 389)
    window.addEventListener('resize', check, { passive: true })
    return () => window.removeEventListener('resize', check)
  }, [])

  // Pending interface-language change: tracks old code + countdown for auto-revert
  const [pendingLang, setPendingLang] = useState<{
    oldCode: string
    newCode: string
    countdown: number
  } | null>(null)

  // Keep a ref to patchUser so the countdown effect always uses the latest version
  const patchUserRef = useRef<(payload: Partial<User>) => Promise<boolean>>(async () => true)

  // Resolves false when the server refused to newly set an unreleased language
  // (ADR-111 D5), true once the change is saved.
  const patchUser = async (payload: Partial<User>): Promise<boolean> => {
    setSaving(true)
    setLangError(null)
    try {
      const { data } = await apiClient.patch<User>('/api/v1/auth/me', payload)
      setUser(data)
      return true
    } catch (err) {
      if (!isLanguageNotReleased(err)) throw err
      setLangError(s.notReleased)
      return false
    } finally {
      setSaving(false)
    }
  }
  patchUserRef.current = patchUser

  // Countdown: tick every second; auto-revert when it reaches 0
  useEffect(() => {
    if (!pendingLang) return
    if (pendingLang.countdown <= 0) {
      patchUserRef.current({ interface_language: pendingLang.oldCode })
      setLocale(resolveLocale(pendingLang.oldCode))
      setPendingLang(null)
      return
    }
    const t = setTimeout(
      () => setPendingLang((p) => (p ? { ...p, countdown: p.countdown - 1 } : null)),
      1000,
    )
    return () => clearTimeout(t)
  }, [pendingLang, setLocale])

  const handleInterfaceLangChange = (code: string) => {
    const oldCode = user.interface_language ?? 'en'
    if (code === oldCode) return
    patchUser({ interface_language: code }).then((saved) => {
      // Refused: put the screen back as it was; nothing was stored.
      if (!saved) {
        setLocale(resolveLocale(oldCode))
        setPendingLang(null)
      }
    })
    setLocale(resolveLocale(code))
    setPendingLang({ oldCode, newCode: code, countdown: CONFIRM_SECONDS })
  }

  const handleKeepLang = () => setPendingLang(null)

  const handleRevertLang = () => {
    if (!pendingLang) return
    patchUser({ interface_language: pendingLang.oldCode })
    setLocale(resolveLocale(pendingLang.oldCode))
    setPendingLang(null)
  }

  // Native name of the pending new language (for the bilingual confirmation),
  // and its table: the prompt shows once that table has loaded, so it never
  // appears in the language being left.
  const newLangNativeName = pendingLang ? nativeNameOf(pendingLang.newCode) : ''
  const newTable = pendingLang ? APP_TRANSLATIONS[pendingLang.newCode] : undefined

  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 24px',
        maxWidth: 520,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>
        {s.title}
      </h2>

      {/* ── Interface language ──────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        {/* Globe icon makes this row scannable without reading the label */}
        <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Globe size={16} aria-hidden />
          {s.interfaceTitle}
        </p>
        {/* Permanent EN subtitle so lost users in a non-EN locale can find this control */}
        {locale !== 'en' && (
          <p style={{ margin: '2px 0 0 24px', fontSize: 12, fontWeight: 400, letterSpacing: '0.01em', color: 'var(--text-secondary)', opacity: 0.8 }}>
            Interface language
          </p>
        )}
        <p style={{ margin: '8px 0 12px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {s.interfaceDesc}
        </p>

        {/* Select — options always labeled in their own language (nativeName) */}
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <select
            value={heldInterface ?? ''}
            onChange={(e) => handleInterfaceLangChange(e.target.value)}
            disabled={saving}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              paddingLeft: 16,
              paddingRight: 32,
              paddingTop: 9,
              paddingBottom: 9,
              border: '1px solid var(--border)',
              borderRadius: 50,
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'var(--font-ui)',
              cursor: saving ? 'wait' : 'pointer',
              outline: 'none',
              minWidth: 160,
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
            onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
          >
            {/* Each option is always labeled in the language it represents */}
            {heldInterface && !heldListed && (
              <option value={heldInterface}>{nativeNameOf(heldInterface)}</option>
            )}
            {interfaceOptions.map(({ code, nativeName }) => (
              <option key={code} value={code}>{nativeName}</option>
            ))}
          </select>
          <span style={{ position: 'absolute', right: 10, pointerEvents: 'none', color: 'var(--text-secondary)', fontSize: 12 }} aria-hidden>▾</span>
        </div>

        {langError && (
          <p role="alert" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--danger)' }}>{langError}</p>
        )}

        {/* Bilingual confirmation banner — shown after a language switch */}
        {pendingLang && newTable && (
          <>
            {/* Keyframe only — no media queries here; responsive layout is JS-driven via isNarrowBanner */}
            <style>{`@keyframes cs-lang-countdown { to { transform: scaleX(0); } }`}</style>
            <div
              role="alert"
              aria-live="assertive"
              style={{
                marginTop: 12,
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: '18px 18px 16px',
                boxShadow: 'var(--shadow-card)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Accent hairline spine — reference: .lang-banner::before width:3px background:var(--accent) */}
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 3, background: 'var(--accent)' }} aria-hidden />

              {/* Label row — reference: .lang-banner__label display:flex align-items:center gap:7px font-size:11px font-weight:600 letter-spacing:0.07em uppercase color:var(--text-secondary)
                  Text nodes wrapped in <span> so every child is an element, preventing anonymous-flex-item sizing surprises */}
              <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                {/* globe: reference .lang-banner__globe width:15px height:15px flex:0 0 auto color:var(--accent) */}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: '0 0 auto', color: 'var(--accent)' }} aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                {revertPromptParts(newTable, pendingLang.newCode, (l) => l.interfaceTitle).map((part, i) => (
                  <Fragment key={i}>
                    {/* .sep: reference opacity:0.45 font-weight:400 */}
                    {i > 0 && <span style={{ opacity: 0.45, fontWeight: 400 }}>·</span>}
                    <span>{part}</span>
                  </Fragment>
                ))}
              </p>

              {/* Hero value — reference: .lang-banner__value font-family:var(--font-content) font-weight:600 font-size:26px line-height:1.2 margin:8px 0 0 */}
              <p style={{ margin: '8px 0 0', fontFamily: 'var(--font-content)', fontWeight: 600, fontSize: 26, lineHeight: 1.2, color: 'var(--text-primary)' }}>
                {newLangNativeName}
              </p>

              {/* Countdown — reference: .lang-banner__track height:3px border-radius:3px background:var(--accent-soft) overflow:hidden
                           .lang-banner__fill background:var(--accent) transform-origin:left animation:lang-countdown 8s linear forwards
                           .lang-banner__countdown margin:8px 0 0 font-size:12px line-height:1.4 color:var(--text-secondary) */}
              <div style={{ marginTop: 14 }}>
                <div style={{ height: 3, borderRadius: 3, backgroundColor: 'var(--accent-soft)', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      backgroundColor: 'var(--accent)',
                      borderRadius: 3,
                      transformOrigin: 'left',
                      animation: 'cs-lang-countdown 8s linear forwards',
                    }}
                  />
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
                  {revertPromptText(newTable, pendingLang.newCode, (l) => l.revertCountdown(pendingLang.countdown))}
                </p>
              </div>

              {/* Actions — reference: .lang-banner__actions display:flex gap:8px margin-top:14px
                           .lang-banner__btn flex:1 1 0 min-width:0 height:40px border-radius:var(--radius-pill) font:600 14px/1 var(--font-ui) padding:0 12px
                  Responsive: stacked column with Keep on top at ≤389px, side-by-side row at ≥390px.
                  JS-driven (isNarrowBanner) — CSS @media in injected <style> tags is unreliable in nested React trees. */}
              <div style={{ display: 'flex', flexDirection: isNarrowBanner ? 'column' : 'row', gap: 8, marginTop: 14 }}>
                {/* Revert — reference: .lang-banner__btn--revert { background:var(--bg-surface); border:1px solid var(--border); color:var(--text-secondary) }
                    appearance:none removes Tailwind preflight's `button{appearance:button}` (preflight.css:372) which imposes the OS native
                    form-control height (~21-24px on Windows Chrome) and blocks inline height:40px from taking effect. */}
                <button
                  type="button"
                  onClick={handleRevertLang}
                  style={{
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    flex: '1 1 0',
                    minWidth: 0,
                    height: 40,
                    minHeight: 40,
                    borderRadius: 'var(--radius-pill)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1,
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-secondary)',
                    transition: 'border-color 0.15s, color 0.15s',
                    order: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                >
                  {revertPromptText(newTable, pendingLang.newCode, (l) => l.revert)}
                </button>
                {/* Keep — reference: .lang-banner__btn--keep { background:var(--accent-fill); border:1px solid var(--accent-fill); color:var(--text-on-accent); box-shadow:var(--shadow-accent) } */}
                <button
                  type="button"
                  onClick={handleKeepLang}
                  style={{
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    flex: '1 1 0',
                    minWidth: 0,
                    height: 40,
                    minHeight: 40,
                    borderRadius: 'var(--radius-pill)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1,
                    backgroundColor: 'var(--accent-fill)',
                    border: '1px solid var(--accent-fill)',
                    color: 'var(--text-on-accent)',
                    boxShadow: 'var(--shadow-accent)',
                    transition: 'background-color 0.15s, border-color 0.15s',
                    order: isNarrowBanner ? -1 : 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-hover)'; e.currentTarget.style.borderColor = 'var(--accent-hover)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-fill)'; e.currentTarget.style.borderColor = 'var(--accent-fill)' }}
                >
                  {revertPromptText(newTable, pendingLang.newCode, (l) => l.keep)}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />

      {/* ── Native language ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <NativeLanguageRow user={user} setUser={setUser} saving={saving} setSaving={setSaving} />
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />

      {/* ── Show translations ───────────────────────────────────────── */}
      <ShowTranslationsRow user={user} setUser={setUser} saving={saving} setSaving={setSaving} />
    </section>
  )
}

// ── Connected accounts section ────────────────────────────────────────────────

const KNOWN_PROVIDERS = [
  { id: 'google', label: 'Google' },
] as const

function ConnectedAccountsSection({ user }: { user: User }) {
  const { tr } = useLocale()
  const s = tr.settings.connected
  const [identities, setIdentities] = useState<LinkedIdentity[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listLinkedIdentities()
      .then((rows) => { if (!cancelled) setIdentities(rows) })
      .catch(() => { if (!cancelled) setIdentities([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const refresh = async () => {
    try { setIdentities(await listLinkedIdentities()) } catch { /* ignore */ }
  }

  const linkedSet = new Set((identities ?? []).map((i) => i.provider))
  // Account is locked-out-on-unlink only when there is no password AND just one identity.
  // Default to "has a password" when the field is missing — older persisted
  // User objects from before the OAuth feature simply do not carry the flag.
  const hasPassword = user.has_password ?? true
  const onlySignInMethod = !hasPassword && (identities?.length ?? 0) <= 1

  const handleConnect = async (provider: string) => {
    setError(null)
    try {
      await startOAuth(provider)
      // startOAuth navigates away on success.
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : s.cannotConnect)
    }
  }

  const handleDisconnect = async (provider: string) => {
    setError(null)
    try {
      await unlinkProvider(provider)
      await refresh()
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail
      const msg =
        status === 409
          ? (typeof detail === 'string'
              ? detail
              : s.cannotDisconnect)
          : s.couldNotDisconnect
      setError(msg)
    }
  }

  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 24px',
        maxWidth: 520,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
        {s.title}
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {s.desc}
      </p>

      {error && (
        <div
          style={{
            backgroundColor: 'var(--danger-soft)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 12px',
            color: 'var(--danger)',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {KNOWN_PROVIDERS.map(({ id, label }) => {
          const linked = linkedSet.has(id)
          const cannotUnlink = linked && onlySignInMethod
          return (
            <div
              key={id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderRadius: 10,
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-page)',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{label}</span>
                {linked && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                    <Check size={14} /> {s.connected}
                  </span>
                )}
              </div>
              {linked ? (
                <button
                  onClick={() => handleDisconnect(id)}
                  disabled={cannotUnlink}
                  title={cannotUnlink ? 'Set a password before disconnecting your only sign-in method.' : undefined}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 50,
                    border: '1px solid var(--border)',
                    backgroundColor: 'transparent',
                    color: cannotUnlink ? 'var(--text-secondary)' : 'var(--text-primary)',
                    fontSize: 13,
                    cursor: cannotUnlink ? 'not-allowed' : 'pointer',
                    opacity: cannotUnlink ? 0.6 : 1,
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  {s.disconnect}
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(id)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 50,
                    border: 'none',
                    backgroundColor: 'var(--accent-fill)',
                    color: 'var(--text-on-accent)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  {s.connect}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {loading && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>{s.loading}</p>
      )}
    </section>
  )
}

